import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';

function Rolling12MonthView({ user, selectedYear, months }) {
  const [rolling12Data, setRolling12Data] = useState({ income: [], expense: [], savings: [] });
  const [loading, setLoading] = useState(true);
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadRolling12MonthData();
  }, [selectedYear]);

  const loadRolling12MonthData = async () => {
    setLoading(true);
    
    try {
      // Load categories
      const incomeSnapshot = await getDocs(collection(db, 'users', user.uid, 'income_categories'));
      const expenseSnapshot = await getDocs(collection(db, 'users', user.uid, 'expense_categories'));
      
      const incomeCategories = incomeSnapshot.docs.map(doc => doc.data().name?.trim()).filter(n => n).sort();
      const expenseCategories = expenseSnapshot.docs.map(doc => doc.data().name?.trim()).filter(n => n).sort();
      
      // Load savings accounts
      const q = query(
        collection(db, 'users', user.uid, 'account_setup'),
        where('active', '==', true)
      );
      const savingsSnapshot = await getDocs(q);
      const savingsAccounts = [];
      
      savingsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const accountName = data.name || '';
        const accountGroup = data.group || '';
        const isSavings = accountGroup.toLowerCase().includes('saving');
        
        if (isSavings && accountName) {
          savingsAccounts.push(accountName);
        }
      });
      
      const savingsCategories = savingsAccounts.sort();
      
      // Load all 12 months data
      const monthlyDataPromises = [];
      for (let m = 0; m < 12; m++) {
        const monthStr = String(m + 1).padStart(2, '0');
        const budgetDocId = `budget_${selectedYear}_${monthStr}`;
        monthlyDataPromises.push(getDoc(doc(db, 'users', user.uid, 'Budgets', budgetDocId)));
      }
      
      const monthlySnapshots = await Promise.all(monthlyDataPromises);
      const monthlyData = monthlySnapshots.map(snap => snap.exists() ? snap.data() : {});
      
      // Determine up to which month to show (current month if current year)
      const maxMonth = (selectedYear === currentYear) ? currentMonth : 11;
      
      // Build income rolling data - DON'T use Math.abs, keep original values
      const incomeData = incomeCategories.map(category => {
        const monthlyActuals = monthlyData.map(data => data.income?.[category]?.actual || 0);
        const monthlyBudgets = monthlyData.map(data => data.income?.[category]?.budget || 0);
        
        // Calculate YTD only up to current month for current year
        const ytdActual = monthlyActuals.slice(0, maxMonth + 1).reduce((sum, val) => sum + val, 0);
        // For current year, only sum budgets up to current month
        // For past years, sum all 12 months
        const totalBudgetYear = (selectedYear === currentYear) 
          ? monthlyBudgets.slice(0, maxMonth + 1).reduce((sum, val) => sum + val, 0)
          : monthlyBudgets.reduce((sum, val) => sum + val, 0);
        const variance = ytdActual - totalBudgetYear;
        
        return {
          category,
          monthlyActuals: monthlyActuals, // Keep all 12 months, don't slice
          ytdActual,
          totalBudgetYear,
          variance,
          maxMonth // Store maxMonth so we know which months to grey out
        };
      });
      
      // Build expense rolling data - DON'T use Math.abs, keep original values (negative)
      const expenseData = expenseCategories.map(category => {
        const monthlyActuals = monthlyData.map(data => data.expense?.[category]?.actual || 0);
        const monthlyBudgets = monthlyData.map(data => data.expense?.[category]?.budget || 0);
        
        const ytdActual = monthlyActuals.slice(0, maxMonth + 1).reduce((sum, val) => sum + val, 0);
        // For current year, only sum budgets up to current month
        // For past years, sum all 12 months
        const totalBudgetYear = (selectedYear === currentYear)
          ? monthlyBudgets.slice(0, maxMonth + 1).reduce((sum, val) => sum + val, 0)
          : monthlyBudgets.reduce((sum, val) => sum + val, 0);
        // For expenses: if actual is MORE negative than budget = overspent (bad)
        // e.g., actual: -5000, budget: -4000, variance: -1000 (spent 1000 more = bad/red)
        const variance = ytdActual - totalBudgetYear;
        
        return {
          category,
          monthlyActuals: monthlyActuals, // Keep all 12 months, don't slice
          ytdActual,
          totalBudgetYear,
          variance,
          maxMonth // Store maxMonth so we know which months to grey out
        };
      });
      
      // Build savings rolling data
      const savingsData = savingsCategories.map(category => {
        const monthlyActuals = monthlyData.map(data => data.savings?.[category]?.actual || 0);
        const monthlyBudgets = monthlyData.map(data => data.savings?.[category]?.budget || 0);
        
        const ytdActual = monthlyActuals.slice(0, maxMonth + 1).reduce((sum, val) => sum + val, 0);
        const totalBudgetYear = (selectedYear === currentYear)
          ? monthlyBudgets.slice(0, maxMonth + 1).reduce((sum, val) => sum + val, 0)
          : monthlyBudgets.reduce((sum, val) => sum + val, 0);
        const variance = ytdActual - totalBudgetYear;
        
        return {
          category,
          monthlyActuals: monthlyActuals,
          ytdActual,
          totalBudgetYear,
          variance,
          maxMonth
        };
      });
      
      setRolling12Data({
        income: incomeData,
        expense: expenseData,
        savings: savingsData,
        maxMonth
      });
      
    } catch (error) {
      console.error('Error loading rolling 12-month data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format number with thousand separators and brackets for negatives
  const formatNumber = (num, decimals = 2) => {
    const isNegative = num < 0;
    const absNum = Math.abs(num);
    const formatted = absNum.toLocaleString('en-GB', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
    return isNegative ? `(${formatted})` : formatted;
  };

  const renderRollingTable = (data, title, colorClass, maxMonth, isCurrentYear) => {
    if (data.length === 0) return null;
    
    // Calculate totals for ALL 12 months
    const monthTotals = [];
    for (let m = 0; m < 12; m++) {
      const total = data.reduce((sum, item) => sum + (item.monthlyActuals[m] || 0), 0);
      monthTotals.push(total);
    }
    
    const grandTotalActual = data.reduce((sum, item) => sum + item.ytdActual, 0);
    const grandTotalBudget = data.reduce((sum, item) => sum + item.totalBudgetYear, 0);
    const grandVariance = grandTotalActual - grandTotalBudget;
    
    const isExpense = colorClass === 'expense';
    
    return (
      <div className="rolling-section">
        <h3 className={`section-title ${colorClass}`}>{title}</h3>
        <div className="rolling-table-wrapper">
          <table className="rolling-table">
            <thead>
              <tr>
                <th className="sticky-col">CATEGORY</th>
                {months.map((month, index) => (
                  <th key={index} className="center">{month}</th>
                ))}
                <th className="right">Total Actual YTD</th>
                <th className="right">{isCurrentYear ? 'Total Budget YTD' : 'Total Budget Year'}</th>
                <th className="right">VAR</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => {
                const isGood = item.variance > 0;
                const isBad = item.variance < 0;
                const itemMaxMonth = item.maxMonth;
                
                return (
                  <tr key={index}>
                    <td className="sticky-col"><strong>{item.category}</strong></td>
                    {item.monthlyActuals.map((actual, mIndex) => {
                      // For current year, grey out future months
                      const isFutureMonth = isCurrentYear && mIndex > itemMaxMonth;
                      return (
                        <td key={mIndex} className="right" style={isFutureMonth ? { color: '#ccc' } : {}}>
                          {actual === 0 ? '-' : formatNumber(actual, 0)}
                        </td>
                      );
                    })}
                    <td className="right"><strong>{formatNumber(item.ytdActual)}</strong></td>
                    <td className="right">{formatNumber(item.totalBudgetYear)}</td>
                    <td className={`right ${isGood ? 'positive' : isBad ? 'negative' : ''}`}>
                      {formatNumber(item.variance)}
                    </td>
                  </tr>
                );
              })}
              <tr className="summary-row">
                <td className="sticky-col"><strong>TOTAL {title.toUpperCase()}</strong></td>
                {monthTotals.map((total, mIndex) => {
                  const isFutureMonth = isCurrentYear && mIndex > maxMonth;
                  return (
                    <td key={mIndex} className="right" style={isFutureMonth ? { color: '#ccc' } : {}}>
                      <strong>{total === 0 ? '-' : formatNumber(total, 0)}</strong>
                    </td>
                  );
                })}
                <td className="right"><strong>{formatNumber(grandTotalActual)}</strong></td>
                <td className="right"><strong>{formatNumber(grandTotalBudget)}</strong></td>
                <td className={`right ${grandVariance > 0 ? 'positive' : grandVariance < 0 ? 'negative' : ''}`}>
                  <strong>{formatNumber(grandVariance)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading-state">Loading 12-month data...</div>;
  }

  const maxMonth = rolling12Data.maxMonth || 11;
  const isCurrentYear = selectedYear === currentYear;

  return (
    <div className="rolling-12-view">
      <h3>12-Month Rolling Analysis for {selectedYear} {isCurrentYear ? `(Jan - ${months[maxMonth]})` : '(Full Year)'}</h3>
      {renderRollingTable(rolling12Data.income, 'Income', 'income', maxMonth, isCurrentYear)}
      {renderRollingTable(rolling12Data.savings, 'Savings', 'savings', maxMonth, isCurrentYear)}
      {renderRollingTable(rolling12Data.expense, 'Expenses', 'expense', maxMonth, isCurrentYear)}
    </div>
  );
}

export default Rolling12MonthView;